import type { User } from '@auth0/auth0-react';

import type { Identity } from 'src/api/endpoints/storeApi.types';
import type { RmClaims } from 'src/api/queries/user';

import * as Sentry from '@sentry/react';
import { isValidKey } from './isValidKey';

export const NOT_RM_USER_EMAIL_SUFFIX = '_not-rm';

const getEmail = (data: User) => data?.email ?? '';
const getEmailParts = (data: User) => {
  const email = getEmail(data);
  const [local, domain] = email.split('@');
  return { full: email, local, domain };
};
const isEmailVerified = (data: User): boolean => {
  // Making some extra checks to assert that email_verified is boolean as expected
  // This comes from an issue with Apple sign in: https://github.com/auth0/auth0-spa-js/issues/833#issuecomment-962986232
  if (typeof data.email_verified === 'boolean') {
    return data.email_verified === true;
  }

  // We are okay with undefined here, it just means not verified
  if (data.email_verified === undefined) return false;

  Sentry.setTag('user.account_provider', getUserAccountProvider(data)); // For easier filtering in Sentry
  Sentry.captureEvent({
    message: 'User.email_verified is not a boolean',
    level: 'warning',
    extra: {
      email_verified: data.email_verified,
      provider: getUserAccountProvider(data),
    },
  });

  // Handle string case just in case
  if (typeof data.email_verified === 'string') {
    return data.email_verified === 'true';
  }

  return false;
};

const getName = (data?: User) => {
  const fallbackName = 'User';
  if (!data) return fallbackName;

  if (data.given_name) {
    const lastName = data.family_name || '';
    return (data.given_name + ' ' + lastName).trim();
  }

  if (data.nickname) {
    return data.nickname;
  }

  if (data.name) {
    return data.name;
  }

  return data.email || fallbackName;
};

const getEmailFromClaims = (data: RmClaims) =>
  data['https://auth.remarkable.com/email'] ?? '';

const getLoginCount = (data: User | RmClaims) =>
  data['https://auth.remarkable.com/logins_count'];

const getLinkableUserId = (data: User | RmClaims) =>
  data['https://auth.remarkable.com/linkable'];

const isLinkable = (data: User | RmClaims) => !!getLinkableUserId(data);

const getTectonicDomain = (data: User | RmClaims | null | undefined) =>
  data?.['https://auth.remarkable.com/tectonic'];

const isAuth0User = (data: User | RmClaims) => !!data.sub?.startsWith('auth0|');

const isGoogleUser = (data: User | RmClaims) =>
  !!data.sub?.startsWith('google-oauth2|');

const isAppleUser = (data: User | RmClaims) => !!data.sub?.startsWith('apple|');

const isFacebookUser = (data: User | RmClaims) =>
  !!data.sub?.startsWith('facebook|');

const isSocialUser = (data: User | RmClaims) =>
  isGoogleUser(data) || isAppleUser(data) || isFacebookUser(data);

const isSAMLUser = (data: User | RmClaims) => !!data.sub?.startsWith('samlp|');

/** Used for tracking */
const getUserAccountProvider = (data: User | RmClaims) => {
  if (isAuth0User(data)) return 'Auth0';
  if (isGoogleUser(data)) return 'Google';
  if (isAppleUser(data)) return 'Apple';
  if (isFacebookUser(data)) return 'Facebook';
  if (isSAMLUser(data)) return 'SAML';
  return 'unknown';
};

const getLinkableAccountProvider = (data: User | RmClaims) => {
  const linkableUser: User = { sub: getLinkableUserId(data) };

  return getUserAccountProvider(linkableUser);
};

const getOrganizationId = (data: User | RmClaims | null | undefined) =>
  data?.['https://auth.remarkable.com/organization'];

const getOrganizationConnection = (data: User | null | undefined) =>
  data?.['https://auth.remarkable.com/connection'];

const getOrganizationDisplayName = (data: User) =>
  data['https://auth.remarkable.com/organization_display_name'];

const isOrganizationMember = (data: User | RmClaims | null | undefined) =>
  !!getOrganizationId(data);

const getLoggedInOrganizationId = (data: User | RmClaims | null | undefined) =>
  data?.['org_id'];

const isLoggedInToOrganization = (data: User | RmClaims) =>
  !!getLoggedInOrganizationId(data);

const getDomainClaimedConnection = (data: User | RmClaims) =>
  data['https://auth.remarkable.com/domain_claimed_by_connection'];

const isDomainClaimedByConnection = (data: User | RmClaims) =>
  !!getDomainClaimedConnection(data);

const hasActiveEnterpriseEnrollment = (data: User | RmClaims) =>
  !!data['https://auth.remarkable.com/enrolling_enterprise_connection'];

const connectionByIdentityProvider = {
  auth0: 'Username-Password-Authentication',
  'google-oauth2': 'google-oauth2',
  apple: 'apple',
  facebook: 'facebook',
};

const getConnectionFromUserId = (data: User | RmClaims) => {
  if (!data || typeof data.sub !== 'string') return;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const provider = data.sub.split('|')[0];

  if (isValidKey(provider, connectionByIdentityProvider)) {
    return connectionByIdentityProvider[provider];
  }

  return;
};

const getSAMLIdentity = (identities: Identity[]) =>
  identities.find((i) => i.provider === 'samlp');

const hasSAMLIdentity = (identities: Identity[]) =>
  !!getSAMLIdentity(identities);

const isAccountRestrictedToAccountLinking = (data: User | RmClaims) =>
  isSAMLUser(data) && isLinkable(data);

const canEditName = (data: User | RmClaims, identities: Identity[]) =>
  isAuth0User(data) && !hasSAMLIdentity(identities);

const canEditEmail = (data: User | RmClaims, identities: Identity[]) =>
  isAuth0User(data) && !hasSAMLIdentity(identities);

const canEditPassword = (data: User | RmClaims, identities: Identity[]) =>
  isAuth0User(data) && !hasSAMLIdentity(identities);

const canMigrateAccountToEnterprise = (data: User | RmClaims) =>
  isLinkable(data) || isDomainClaimedByConnection(data);

const isUnlinkedSamlAccount = (data: User | RmClaims, identities: Identity[]) =>
  isSAMLUser(data) && identities.length === 1;

const getSubscription = (data: User | RmClaims | null | undefined) =>
  data?.['https://auth.remarkable.com/subscription'];

const hasSubscription = (data: User | RmClaims | null | undefined) =>
  !!getSubscription(data);

const isRemarkableUser = (data: User | RmClaims | null | undefined) => {
  if (!data) return false;

  const domains = [
    'remarkable.com',
    'remarkable.no',
    'inbox.testmail.app',
    'rm.guru',
    'remarkable-company.com',
  ];

  const email = getEmail(data);
  const [local, domain] = email.split('@');

  if (!domain) {
    return false;
  }

  const isTestUserThatShouldBeTreatedAsRegularUser = local.includes(
    NOT_RM_USER_EMAIL_SUFFIX
  );

  if (isTestUserThatShouldBeTreatedAsRegularUser) {
    return false;
  }

  // Supports user@sub.remarkable.no as well as user@remarkable.no, but not
  // user@helloremarkable.no.
  const endsWithValidDomain = domains.findIndex(
    (validDomain) =>
      domain.toLowerCase() === validDomain ||
      domain.toLowerCase().endsWith(`.${validDomain}`)
  );

  return endsWithValidDomain !== -1;
};

// Employees also have a name@getremarkable.com alias, which points to the
// same inbox as their @remarkable.no address. It's deliberately kept separate
// from `isRemarkableUser`/`internal` so employees can use this alias to
// appear as non-internal users for testing purposes (e.g. LaunchDarkly
// targeting). Add further alias domains to this list as they're introduced.
const hasRmAliasEmail = (data: User | null | undefined): boolean => {
  if (!data) return false;

  const rmAliasDomains = ['getremarkable.com'];

  const { domain } = getEmailParts(data);
  return !!domain && rmAliasDomains.includes(domain.toLowerCase());
};

const hasBetaAccess = (data: User | null | undefined): boolean =>
  data?.['https://auth.remarkable.com/beta']?.toString().toLowerCase() ===
  'true';

const getPermissions = (data: RmClaims) => data['permissions'];

const hasPermissionReadMembers = (data: RmClaims) =>
  !!getPermissions(data)?.includes('read:members');

const hasPermissionReadOrganization = (data: RmClaims) =>
  !!getPermissions(data)?.includes('read:organization');

const hasPermissionReadOrganizationSettings = (data: RmClaims) =>
  !!getPermissions(data)?.includes('read:organization_settings');

const hasPermissionReadRoleAssignments = (data: RmClaims) =>
  !!getPermissions(data)?.includes('read:role_assignments');

const hasPermissionCreateRoleAssignments = (data: RmClaims) =>
  !!getPermissions(data)?.includes('create:role_assignments');

const hasPermissionDeleteRoleAssignments = (data: RmClaims) =>
  !!getPermissions(data)?.includes('delete:role_assignments');

const hasPermissionReadInvitations = (data: RmClaims) =>
  !!getPermissions(data)?.includes('read:invitations');

const hasPermissionCreateInvitations = (data: RmClaims) =>
  !!getPermissions(data)?.includes('create:invitations');

const hasPermissionUpdateOrgSettings = (data: RmClaims) =>
  !!getPermissions(data)?.includes('update:organization_settings');

const hasPermissionCreateProfiles = (data: RmClaims) =>
  !!getPermissions(data)?.includes('create:profiles');

const hasPermissionReadProfiles = (data: RmClaims) =>
  !!getPermissions(data)?.includes('read:profiles');

const hasPermissionUpdateProfiles = (data: RmClaims) =>
  !!getPermissions(data)?.includes('update:profiles');

const hasPermissionCreateCommands = (data: RmClaims) =>
  !!getPermissions(data)?.includes('create:commands');

const hasPermissionChangeUserStatus = (data: RmClaims) =>
  !!getPermissions(data)?.includes('update:members');

const hasPermissionReadSubscription = (data: RmClaims) =>
  !!getPermissions(data)?.includes('read:subscription');

const hasPermissionUpdateSubscription = (data: RmClaims) =>
  !!getPermissions(data)?.includes('update:subscription');

const hasPermissionDeleteSubscription = (data: RmClaims) =>
  !!getPermissions(data)?.includes('delete:subscription');

const hasPermissionCreateOffboarding = (data: RmClaims) =>
  !!getPermissions(data)?.includes('create:offboarding');

export const userHelpers = {
  canEditEmail,
  canEditName,
  canEditPassword,
  canMigrateAccountToEnterprise,
  getConnectionFromUserId,
  getDomainClaimedConnection,
  getEmail,
  getEmailParts,
  getEmailFromClaims,
  getLinkableAccountProvider,
  getLinkableUserId,
  getLoggedInOrganizationId,
  getLoginCount,
  getName,
  getOrganizationConnection,
  getOrganizationDisplayName,
  getOrganizationId,
  getPermissions,
  getSAMLIdentity,
  getSubscription,
  getTectonicDomain,
  getUserAccountProvider,
  hasActiveEnterpriseEnrollment,
  hasBetaAccess,
  hasPermissionCreateCommands,
  hasPermissionCreateInvitations,
  hasPermissionCreateOffboarding,
  hasPermissionCreateProfiles,
  hasPermissionCreateRoleAssignments,
  hasPermissionDeleteRoleAssignments,
  hasPermissionDeleteSubscription,
  hasPermissionReadInvitations,
  hasPermissionReadMembers,
  hasPermissionReadOrganization,
  hasPermissionReadOrganizationSettings,
  hasPermissionReadProfiles,
  hasPermissionReadRoleAssignments,
  hasPermissionUpdateOrgSettings,
  hasPermissionUpdateProfiles,
  hasPermissionChangeUserStatus,
  hasPermissionReadSubscription,
  hasPermissionUpdateSubscription,
  hasRmAliasEmail,
  hasSAMLIdentity,
  hasSubscription,
  isAccountRestrictedToAccountLinking,
  isAppleUser,
  isAuth0User,
  isDomainClaimedByConnection,
  isEmailVerified,
  isFacebookUser,
  isGoogleUser,
  isLinkable,
  isLoggedInToOrganization,
  isOrganizationMember,
  isRemarkableUser,
  isSAMLUser,
  isSocialUser,
  isUnlinkedSamlAccount,
  NOT_RM_USER_EMAIL_SUFFIX,
};
